import {useMemo, useRef, useState} from "react";
import {Box, Button, IconButton, Stack, Typography, useTheme} from "@mui/material";
import {FiTrash2} from "react-icons/fi";
import {scopesColumns} from "../../constants/dataGridColumns";
import useApiRequest from "../../hooks/useApiRequest";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import {scopeToString} from "../../utils/formatData";
import {snackbar} from "../../utils/snackbar";
import ListView from "../ListView/ListView";
import ConfirmDialog from "../ui/ConfirmDialog";
import RoleScopesFilterBar from "./RoleScopesFilterBar";
import ScopeSearchAutocomplete from "../Scopes/ScopeSearchAutocomplete";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const RoleScopesList = ({roleId}) => {
  const theme = useTheme();
  const [scopes, setScopes] = useState([]);
  const [isSettingNewScope, setIsSettingNewScope] = useState(false);
  const [selectedScope, setSelectedScope] = useState(null);
  const lastSearchParams = useRef({});

  const {loading, makeRequest} = useApiRequest({baseUrl: "/roles", resource: "roles"});

  const assignedScopeIds = useMemo(() => scopes.map((s) => s.scope_id), [scopes]);

  const handleSearch = async (params) => {
    lastSearchParams.current = params;
    const result = await makeRequest("get", `/${roleId}/scopes`, null, {params});
    if (result?.success) {
      setScopes(normalizeArray(result.data));
    }
    return [];
  };

  const handleToggleAssign = () => {
    setIsSettingNewScope((prev) => !prev);
    setSelectedScope(null);
  };

  const handleAccept = async () => {
    if (!selectedScope?.id) return;

    const result = await makeRequest("post", `/${roleId}/scopes`, {scope_id: selectedScope.id});
    if (!result?.success) return;

    snackbar.success("Scope asignado al rol correctamente.");
    setIsSettingNewScope(false);
    setSelectedScope(null);
    await handleSearch(lastSearchParams.current);
  };

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (scopeId) => {
    const result = await makeRequest("delete", `/${roleId}/scopes/${scopeId}`);
    if (result?.success) {
      await handleSearch(lastSearchParams.current);
    }
  });

  const handleOpenDeleteDialog = (scopeId) => {
    const found = scopes.find((s) => s.scope_id === scopeId);
    openDialog(scopeId, (found && scopeToString(found)) || "");
  };

  const columns = [
    ...scopesColumns,
    {
      field: "actions",
      headerName: "Acciones",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="delete"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDeleteDialog(params.row.scope_id);
          }}
        >
          <FiTrash2 />
        </IconButton>
      )
    }
  ];

  if (!roleId) {
    return (
      <Box sx={{padding: 2}}>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          No hay rol seleccionado
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{width: "100%", marginTop: 3}}>
      <Typography variant="h6" gutterBottom sx={{fontWeight: "bold", color: theme.palette.text.primary}}>
        Scopes Asociados
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <RoleScopesFilterBar roleId={roleId} onSearch={handleSearch} />
        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}}>
          {isSettingNewScope && (
            <>
              <ScopeSearchAutocomplete
                value={selectedScope}
                onChange={setSelectedScope}
                excludeIds={assignedScopeIds}
              />
              <Button variant="contained" onClick={handleAccept} disabled={!selectedScope?.id}>
                Aceptar
              </Button>
            </>
          )}
          <Button variant="contained" color={isSettingNewScope ? "error" : "primary"} onClick={handleToggleAssign}>
            {isSettingNewScope ? "Cancelar" : "Asignar scope"}
          </Button>
        </Stack>
      </Stack>

      <ListView columns={columns} data={scopes} loading={loading} getRowId={(row) => row.scope_id} />

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="scope"
        entityLabel={itemName}
      />
    </Box>
  );
};

export default RoleScopesList;
